import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "antd";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useHistory } from "react-router-dom";
import { SearchInput } from "../Input";
import ProtectedComponent from "../_auth/ProtectedComponent";
import {
  PERMISSION_VIEW_CORE_USERS,
  PERMISSION_VIEW_QAM_AUDITS,
  PERMISSION_VIEW_QAM_CORRECTIVE_ACTIONS,
  PERMISSION_VIEW_TRG,
  PERMISSION_VIEW_TRG_PLANNING
} from "../_auth/permissions/permissions";
import { usePermission } from "../_auth/permissions/usePermission";
import GeneralSearchOverlay, { entitiesSearch } from "./GeneralSearchOverlay";

const GeneralSearch = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const permission = usePermission();
  const [selectedEntity, setSelectedEntity] = useState(entitiesSearch.find((item) => permission.canDo(item.gates)));
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [open, setOpen] = useState(false);

  const onSelect = (selected) => {
    setSelectedEntity(selected);
    setDropdownVisible(false);
  };

  const onSearch = useCallback(
    (value) => {
      if (!selectedEntity?.path) {
        return;
      }

      history.push({
        pathname: selectedEntity.path,
        search: value ? `search=${value}` : "",
        state: { generalSearch: true }
      });
    },
    [selectedEntity, history]
  );

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const debouncedOnSearch = useMemo(
    () => debounce((value) => onSearchRef.current(value), 1000),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    return () => {
      debouncedOnSearch.cancel();
    };
  }, [debouncedOnSearch]);

  const renderPopup = useCallback(() => <GeneralSearchOverlay onSelect={onSelect} />, []);

  return (
    <ProtectedComponent
      gates={[
        PERMISSION_VIEW_CORE_USERS,
        PERMISSION_VIEW_QAM_AUDITS,
        PERMISSION_VIEW_QAM_CORRECTIVE_ACTIONS,
        PERMISSION_VIEW_TRG,
        PERMISSION_VIEW_TRG_PLANNING
      ]}
    >
      <div className={classNames("general-search", { open })}>
        {open && (
          <button
            type="button"
            className="hide-search"
            onClick={() => setOpen(false)}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
          >
            <i className="ti-close" />
          </button>
        )}
        <a
          href="/#"
          className="icon-link white"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          <i className="ti-search" />
        </a>
        <div className="search-field-group">
          <SearchInput
            placeholder={t("container.general_words.search_for")}
            onChange={(e) => debouncedOnSearch(e.target.value)}
            onSearch={onSearch}
          />
          <Dropdown
            popupRender={renderPopup}
            trigger={["click"]}
            open={dropdownVisible}
            getPopupContainer={(el) => el}
            onOpenChange={(vis) => setDropdownVisible(vis)}
            classNames={{ root: "general-search-overlay" }}
            placement="bottomRight"
          >
            <div>
              <i className="ti-angle-down" />
              <span>{t(selectedEntity?.title)}</span>
            </div>
          </Dropdown>
        </div>
      </div>
    </ProtectedComponent>
  );
};

export default GeneralSearch;
